/**
 * ShoutoutsReelModal
 *
 * Fullscreen, IG/TikTok-Stories-style player for celeb shout-outs.
 *
 * Controls (consistent with the patterns users already know):
 *   - Tap right half       → advance to next clip
 *   - Tap left half (35%)  → previous clip
 *   - Swipe down           → dismiss
 *   - ▶ VIDEO / ▦ CARD     → toggle between video and the static card art
 *                            (matches BLKCardFrame pattern used on collectibles)
 *   - Mute / unmute toggle (top-right)
 *   - Large red ✕ close button (top-right) — also reachable via ESC
 *   - Progress bars at the top — one bar per clip, the active one fills
 *     in real-time as the video plays.
 *
 * Notes:
 *   - Video starts muted (web autoplay policy). Mute pref persists.
 *   - When a clip ends naturally OR errors, we auto-advance.
 *   - `onMarkSeen(id)` fires the moment a clip becomes active so the
 *     navbar ring pulse drops to zero as the user is served the content.
 *   - In CARD mode the video is paused so the audio doesn't keep playing
 *     under a static image. Returning to VIDEO mode resumes from where we
 *     left off (we read `currentTime` from the same <video> element).
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { X, Volume2, VolumeX, BadgeCheck, Play, LayoutGrid, ExternalLink, ChevronDown } from "lucide-react";
import { API } from "../../utils";

const MUTE_KEY = "blk_shoutouts_reel_muted";
const VIEW_TRACKED_KEY = "blk_shoutouts_viewed_session"; // sessionStorage

const readMutePref = () => {
  try {
    return localStorage.getItem(MUTE_KEY) !== "0";
  } catch {
    return true;
  }
};

const writeMutePref = (muted) => {
  try {
    localStorage.setItem(MUTE_KEY, muted ? "1" : "0");
  } catch {
    /* ignore */
  }
};

// Session-scoped view tracking — one increment per shout-out per browser
// session so we don't inflate the counter every time a user reopens the reel.
const readViewedSet = () => {
  try {
    const raw = sessionStorage.getItem(VIEW_TRACKED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr) : new Set();
  } catch {
    return new Set();
  }
};

const markViewed = (id) => {
  try {
    const set = readViewedSet();
    set.add(id);
    sessionStorage.setItem(VIEW_TRACKED_KEY, JSON.stringify(Array.from(set)));
  } catch {
    /* ignore */
  }
};

const trackView = (id) => {
  // Public endpoint — silently swallow errors so analytics never blocks UX.
  try {
    const viewed = readViewedSet();
    if (viewed.has(id)) return;
    markViewed(id);
    axios
      .post(`${API}/shoutouts/${id}/track-view`, null, {
        withCredentials: false,
        timeout: 4000,
      })
      .catch(() => {});
  } catch {
    /* ignore */
  }
};

const trackTap = (id) => {
  try {
    axios
      .post(`${API}/shoutouts/${id}/track-tap`, null, {
        withCredentials: false,
        timeout: 4000,
      })
      .catch(() => {});
  } catch {
    /* ignore */
  }
};

const ShoutoutsReelModal = ({ shoutouts, onClose, onMarkSeen }) => {
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(() => readMutePref());
  const [progress, setProgress] = useState(0);
  const [mode, setMode] = useState("video"); // "video" | "card"
  const [videoState, setVideoState] = useState("loading"); // "loading" | "ready" | "error"
  // Mobile swipe-down educational hint — fades in shortly after mount,
  // fades out at ~3.5s. Reinforced by a persistent iOS-style grabber
  // pill at the top (no fade) so the affordance is always discoverable
  // even after the text hint is gone.
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const videoRef = useRef(null);
  const touchStartY = useRef(null);
  const touchStartX = useRef(null);
  // True if the video was playing when the user grabbed the scrubber —
  // we pause during scrub and resume on release so audio doesn't sputter.
  const scrubbingWasPlaying = useRef(false);
  const navigate = useNavigate();
  const total = shoutouts.length;
  const current = shoutouts[idx];

  useEffect(() => {
    // Slight in-delay so the hint reads as an *overlay tip*, not part
    // of the video itself. Auto-dismisses well before the average clip
    // length so it doesn't compete with content.
    const inT = setTimeout(() => setShowSwipeHint(true), 400);
    const outT = setTimeout(() => setShowSwipeHint(false), 3400);
    return () => {
      clearTimeout(inT);
      clearTimeout(outT);
    };
  }, []);

  // Mark current clip as seen + fire a session-deduped view track.
  useEffect(() => {
    if (current?.id) {
      onMarkSeen?.(current.id);
      trackView(current.id);
    }
    setMode("video");
  }, [current?.id, onMarkSeen]);

  // Lock body scroll while modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // ESC closes; arrows navigate
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowRight") setIdx((i) => Math.min(i + 1, total - 1));
      else if (e.key === "ArrowLeft") setIdx((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [total, onClose]);

  const advance = useCallback(() => {
    setIdx((i) => {
      if (i >= total - 1) {
        onClose?.();
        return i;
      }
      return i + 1;
    });
  }, [total, onClose]);

  // Reset state + attach video listeners — ONLY when the active clip
  // changes. We intentionally exclude `mode` here so toggling between
  // VIDEO and CARD never re-fires the loading spinner / re-attaches
  // listeners. If we did include `mode`, returning to video would reset
  // `videoState` back to "loading" but the canplay/loadeddata events
  // would never fire again (the video is already buffered), so the
  // loading overlay would sit on top of the playing video forever —
  // that's the "dead screen with phantom audio" bug.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    setProgress(0);
    // If the browser already has enough data to play (readyState >= 2
    // HAVE_CURRENT_DATA), skip the loading shim entirely.
    setVideoState(v.readyState >= 2 ? "ready" : "loading");
    const onTime = () => {
      if (v.duration && isFinite(v.duration)) {
        setProgress(Math.min(v.currentTime / v.duration, 1));
      }
    };
    const onReady = () => setVideoState("ready");
    const onEnded = () => advance();
    const onError = () => {
      setVideoState("error");
      // Don't auto-advance immediately on error — give the user a chance
      // to see the fallback UI and close the modal themselves. Auto-skip
      // after 2.5s so a broken clip in the middle of a reel doesn't trap
      // them; that's only long enough to read the error.
      setTimeout(() => {
        // Best-effort advance; the modal may have already closed.
        advance();
      }, 2500);
    };
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("canplay", onReady);
    v.addEventListener("loadeddata", onReady);
    v.addEventListener("ended", onEnded);
    v.addEventListener("error", onError);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("canplay", onReady);
      v.removeEventListener("loadeddata", onReady);
      v.removeEventListener("ended", onEnded);
      v.removeEventListener("error", onError);
    };
  }, [idx, advance]);

  // Keep the <video> element's mute property in sync with the user's
  // preference — independent of mode/idx so toggling mute mid-reel
  // doesn't bounce the spinner.
  useEffect(() => {
    const v = videoRef.current;
    if (v) v.muted = muted;
  }, [muted]);

  // Play / pause driven purely by the VIDEO ↔ CARD toggle (and clip
  // changes). The element stays mounted, so `currentTime` is preserved
  // across mode switches — returning to VIDEO resumes from where the
  // user left off instead of re-buffering.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (mode === "video") {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } else {
      try {
        v.pause();
      } catch {}
    }
  }, [mode, idx]);

  const toggleMute = (e) => {
    e?.stopPropagation?.();
    setMuted((m) => {
      const next = !m;
      writeMutePref(next);
      return next;
    });
  };

  const handleTapZone = (zone) => (e) => {
    e.stopPropagation();
    // CARD mode swallows tap-zone navigation. The user wants the card
    // image itself to be the "back to video" affordance (handled in the
    // card image's own onClick), not a sneaky tap that flips them to a
    // different shout-out behind the card. Without this guard, tapping
    // anywhere on the card image area triggered a prev/next zone
    // because the tap zones sit at z-10 below the card (z-20).
    if (mode === "card") return;
    if (zone === "next") {
      if (idx >= total - 1) onClose?.();
      else setIdx(idx + 1);
    } else if (zone === "prev") {
      // Never restart the current clip — that was the "I tapped the
      // screen and it restarted the video" bug. If we're already at the
      // first clip, treat prev as a no-op so the user can keep watching.
      // (Native IG/WhatsApp behaviour: prev on first clip just keeps
      // playing; never seeks to 0.)
      if (idx > 0) setIdx(idx - 1);
    }
  };

  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchStartY.current = t.clientY;
    touchStartX.current = t.clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartY.current == null) return;
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dy = t.clientY - touchStartY.current;
    const dx = (t.clientX || 0) - (touchStartX.current || 0);
    if (dy > 80 && Math.abs(dy) > Math.abs(dx) * 1.5) {
      onClose?.();
    }
    touchStartY.current = null;
    touchStartX.current = null;
  };

  if (!current) return null;

  const hasCard = Boolean(current.card_image_url);
  const showCardView = mode === "card" && hasCard;

  // The navbar (which renders <StoriesRing /> → this modal) has
  // `backdrop-blur-md`, which per the CSS containing-block spec
  // anchors `position: fixed` descendants to the navbar instead of
  // the viewport. That made the modal pin to the top of the page on
  // desktop. Portaling to <body> escapes the navbar's containing
  // block so `fixed inset-0` resolves against the real viewport.
  const modalNode = (
    <div
      data-testid="shoutouts-reel-modal"
      // Dark scrim covers the entire viewport. Click anywhere outside
      // the inner card = dismiss (universal modal UX). We stopPropagation
      // on the inner card's pointer events so clicks INSIDE the video
      // area never bubble up here and accidentally close.
      // Using flex centering for the inner card so we don't fight
      // Tailwind class-ordering on absolute-positioning utilities.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-sm flex items-stretch md:items-center justify-center"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="dialog"
      aria-modal="true"
      aria-label="Celebrity shout-outs"
    >
      <div
        // Inner card.
        //   - Mobile: takes the full viewport (`w-full h-[100svh]`)
        //   - Desktop: 420×820 floating card with a bright orange-glow
        //     border so it's IMMEDIATELY obvious you're inside a modal
        //     (no more "the page went black" confusion).
        // No absolute-positioning utilities are used here — flex on the
        // parent does all the centering work, so we can't lose width to
        // a `top/left/right/bottom` class-ordering collision.
        onMouseDown={(e) => e.stopPropagation()}
        className="
          relative
          w-full h-[100svh]
          bg-[#0a0a0c] overflow-hidden
          md:w-[420px] md:max-w-[90vw]
          md:h-[820px] md:max-h-[90vh]
          md:rounded-2xl
          md:shadow-[0_0_50px_rgba(249,115,22,0.45)]
          md:ring-1 md:ring-orange-500/40
        "
        style={{ flexShrink: 0 }}
      >
        {/* VIDEO LAYER — always mounted so currentTime survives mode toggles. */}
        <video
          ref={videoRef}
          src={current.video_url}
          poster={current.poster_url || current.avatar_url || undefined}
          playsInline
          autoPlay
          muted={muted}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-200 ${
            showCardView ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
          data-testid="shoutouts-reel-video"
        />

        {/* LOADING state — visible spinner so the modal never looks
            "broken" while the video is buffering. Drops out once
            canplay / loadeddata fires (videoState becomes "ready"). */}
        {videoState === "loading" && !showCardView && (
          <div
            className="absolute inset-0 z-[1] flex flex-col items-center justify-center bg-gradient-to-br from-[#1a1a24] via-[#0a0a0c] to-[#1a1a24] gap-3 pointer-events-none"
            data-testid="shoutouts-reel-loading"
          >
            <div className="w-10 h-10 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
            <span className="text-white/70 text-xs font-medium tracking-wide uppercase">
              Loading shout-out…
            </span>
          </div>
        )}

        {/* ERROR state — if the video fails to load entirely, show a
            clear fallback so the user knows what happened AND has a
            visible close button. Auto-advances after 2.5s. */}
        {videoState === "error" && !showCardView && (
          <div
            className="absolute inset-0 z-[1] flex flex-col items-center justify-center bg-gradient-to-br from-[#1a1a24] via-[#0a0a0c] to-[#1a1a24] gap-3 px-6 text-center pointer-events-none"
            data-testid="shoutouts-reel-error"
          >
            {current.avatar_url && (
              <img
                src={current.avatar_url}
                alt=""
                className="w-16 h-16 rounded-full object-cover border-2 border-orange-500/50"
              />
            )}
            <div className="text-white font-semibold text-sm">
              {current.celeb_name}
            </div>
            <div className="text-white/60 text-xs max-w-xs">
              This shout-out couldn't load right now — moving to the next one in a moment.
            </div>
          </div>
        )}

        {/* CARD LAYER — only when a card image was uploaded AND user picks card mode.
            z-20 so it sits above the tap zones (z-10) — tapping the card
            should never trigger prev/next, it should just flip back to
            video. The image itself is wrapped in a button so the whole
            area is the "back to video" affordance — way more discoverable
            than the small VIDEO toggle pill at the bottom. */}
        {showCardView && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMode("video");
            }}
            className="absolute inset-0 w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1a1a24] via-black to-[#0a0a0c] z-20 cursor-pointer"
            data-testid="shoutouts-reel-card-view"
            aria-label="Back to video"
          >
            <img
              src={current.card_image_url}
              alt={`${current.celeb_name} card`}
              className="max-w-[88%] max-h-[78%] object-contain drop-shadow-[0_0_24px_rgba(249,115,22,0.35)] pointer-events-none"
            />
            {/* Tiny visual hint so the user discovers the tap-to-go-back
                affordance. Sits in the bottom-left corner, just above
                the VIDEO/CARD toggle pill, doesn't fight the artwork. */}
            <span
              className="pointer-events-none absolute bottom-16 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.16em] font-bold text-white/55"
              data-testid="shoutouts-reel-card-back-hint"
            >
              tap card to resume video
            </span>
          </button>
        )}

        {/* Tap zones — sit below the chrome and the toggle */}
        <button
          type="button"
          aria-label="Previous shout-out"
          onClick={handleTapZone("prev")}
          className="absolute inset-y-0 left-0 w-[35%] z-10"
          data-testid="shoutouts-reel-prev-zone"
        />
        <button
          type="button"
          aria-label="Next shout-out"
          onClick={handleTapZone("next")}
          className="absolute inset-y-0 right-0 w-[65%] z-10"
          data-testid="shoutouts-reel-next-zone"
        />

        {/* Top progress bars.
            The ACTIVE segment doubles as a timeline scrubber — drag or
            tap anywhere along it to seek within the current clip.
            Pointer-events are enabled only on the active bar so prior /
            future clips' bars stay decorative and don't intercept taps
            meant for the close button. */}
        <div
          className="absolute top-0 left-0 right-0 flex gap-1 px-3 z-20 pointer-events-none"
          style={{ paddingTop: "max(env(safe-area-inset-top, 0px), 12px)" }}
          data-testid="shoutouts-reel-progress"
        >
          {shoutouts.map((s, i) => {
            const fill = i < idx ? 1 : i === idx ? progress : 0;
            const isActive = i === idx;
            // Only the active segment is interactive. We enlarge its
            // hit area vertically (py-2.5) without enlarging the visible
            // bar (still 3px) so the user has a comfortable touch target
            // on mobile without the layout shifting.
            return isActive ? (
              <button
                key={s.id}
                type="button"
                data-testid={`shoutouts-reel-scrubber-${i}`}
                aria-label="Seek within current clip"
                onClick={(e) => {
                  e.stopPropagation();
                  const rect = e.currentTarget.getBoundingClientRect();
                  const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                  const v = videoRef.current;
                  if (v && v.duration && isFinite(v.duration)) {
                    v.currentTime = pct * v.duration;
                    setProgress(pct);
                  }
                }}
                onPointerDown={(e) => {
                  // Capture pointer so dragging outside the bar still
                  // updates the scrub position — matches native video
                  // scrubber behaviour.
                  e.currentTarget.setPointerCapture(e.pointerId);
                  const v = videoRef.current;
                  if (v && !v.paused) {
                    // Pause while scrubbing so audio doesn't sputter on
                    // every micro-seek; we resume on pointer up.
                    scrubbingWasPlaying.current = true;
                    try { v.pause(); } catch { /* noop */ }
                  }
                }}
                onPointerMove={(e) => {
                  if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                  const v = videoRef.current;
                  if (v && v.duration && isFinite(v.duration)) {
                    v.currentTime = pct * v.duration;
                    setProgress(pct);
                  }
                }}
                onPointerUp={(e) => {
                  try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ }
                  const v = videoRef.current;
                  if (v && scrubbingWasPlaying.current && mode === "video") {
                    const p = v.play();
                    if (p && typeof p.catch === "function") p.catch(() => {});
                  }
                  scrubbingWasPlaying.current = false;
                }}
                onPointerCancel={(e) => {
                  try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ }
                  scrubbingWasPlaying.current = false;
                }}
                className="flex-1 py-2.5 -my-2.5 pointer-events-auto cursor-pointer group focus:outline-none touch-none"
              >
                <div className="h-[3px] rounded-full bg-white/25 overflow-hidden group-hover:bg-white/35 group-active:bg-white/40 transition-colors relative">
                  <div
                    className="h-full bg-white"
                    style={{ width: `${Math.round(fill * 100)}%`, transition: "none" }}
                  />
                  {/* Scrubber thumb — visible on hover/active so the
                      user knows the bar is interactive. Tiny dot, no
                      layout shift. */}
                  <div
                    className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-white shadow opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity"
                    style={{ left: `${Math.round(fill * 100)}%` }}
                  />
                </div>
              </button>
            ) : (
              <div
                key={s.id}
                className="flex-1 h-[3px] rounded-full bg-white/25 overflow-hidden"
              >
                <div
                  className="h-full bg-white"
                  style={{
                    width: `${Math.round(fill * 100)}%`,
                    transition: "width 200ms linear",
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Top chrome — identity row + mute + LARGE close */}
        <div
          className="absolute top-0 left-0 right-0 px-4 z-20 flex items-center gap-3 pointer-events-none"
          style={{
            paddingTop:
              "calc(max(env(safe-area-inset-top, 0px), 12px) + 14px)",
          }}
        >
          {current.avatar_url && (
            <img
              src={current.avatar_url}
              alt=""
              className="w-9 h-9 rounded-full object-cover border border-white/30 flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-white font-semibold text-sm truncate">
              <span className="truncate">{current.celeb_name}</span>
              {current.is_verified && (
                <BadgeCheck
                  className="w-4 h-4 text-amber-400 flex-shrink-0"
                  data-testid="shoutouts-reel-verified"
                />
              )}
            </div>
            {current.celeb_handle && (
              <div className="text-white/70 text-xs truncate">
                {current.celeb_handle.startsWith("@")
                  ? current.celeb_handle
                  : `@${current.celeb_handle}`}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={toggleMute}
            className="pointer-events-auto p-2 rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-colors"
            data-testid="shoutouts-reel-mute"
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          {/* LARGE close button — red so it can't be missed */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
            className="pointer-events-auto w-10 h-10 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg shadow-red-500/40 transition-colors"
            data-testid="shoutouts-reel-close"
            aria-label="Close shout-outs"
          >
            <X className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>

        {/* iOS-style grabber pill — persistent, ALWAYS visible on mobile.
            Universally understood as "you can drag this down to dismiss".
            Sits above the progress bars at the very top of the modal.
            Pointer-events-none so it never blocks the underlying tap zones. */}
        <div
          className="md:hidden absolute left-1/2 -translate-x-1/2 z-30 pointer-events-none"
          style={{ top: "max(env(safe-area-inset-top, 0px), 6px)" }}
          aria-hidden="true"
          data-testid="shoutouts-reel-grabber"
        >
          <div className="w-9 h-1 rounded-full bg-white/55" />
        </div>

        {/* Educational hint pill — fades in for the first ~3 seconds so
            new visitors learn the swipe gesture, then quietly disappears.
            Position is BELOW the identity row (avatar/name/mute/X) so it
            never overlaps any interactive chrome. Pointer-events-none. */}
        <div
          className={`md:hidden absolute left-1/2 -translate-x-1/2 z-30 pointer-events-none transition-opacity duration-500 ${
            showSwipeHint ? "opacity-100" : "opacity-0"
          }`}
          style={{
            top: "calc(max(env(safe-area-inset-top, 0px), 12px) + 78px)",
          }}
          aria-hidden="true"
          data-testid="shoutouts-reel-swipe-hint"
        >
          <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-sm text-white text-[11px] font-medium shadow-lg shadow-black/40">
            <ChevronDown className="w-3.5 h-3.5 animate-bounce" strokeWidth={2.5} />
            Swipe down to close
          </div>
        </div>

        {/* CTA "Visit" button — fires tap-through tracking when clicked.
            Always rendered when a cta_url is set so analytics can compare
            view→tap conversion across celebs. Defaults to /drops when the
            admin didn't set one — gives every celeb a CTA out of the box. */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!current?.id) return;
            trackTap(current.id);
            const target = (current.cta_url || "/drops").trim();
            // External vs internal — keep the modal-close lifecycle clean.
            onClose?.();
            if (/^https?:\/\//i.test(target)) {
              window.open(target, "_blank", "noopener,noreferrer");
            } else {
              navigate(target);
            }
          }}
          data-testid="shoutouts-reel-cta"
          className="absolute left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white text-xs font-bold shadow-lg shadow-orange-500/40 touch-manipulation active:scale-95 transition-transform"
          style={{
            bottom: hasCard
              ? "calc(max(env(safe-area-inset-bottom, 0px), 18px) + 48px)"
              : "max(env(safe-area-inset-bottom, 0px), 18px)",
          }}
          aria-label="Visit the drop linked to this shout-out"
        >
          {(current.cta_label || "VISIT DROPS")} <ExternalLink className="w-3 h-3" />
        </button>

        {/* VIDEO ↔ CARD toggle — only when a card image exists. Mirrors the
            BLKCardFrame collectible toggle so users get the same pattern. */}
        {hasCard && (
          <div
            className="absolute z-30 left-1/2 -translate-x-1/2 pointer-events-auto"
            style={{ bottom: "max(env(safe-area-inset-bottom, 0px), 18px)" }}
          >
            <div className="flex bg-black/60 backdrop-blur-sm border border-white/15 rounded-full overflow-hidden">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setMode("video");
                }}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                  mode === "video"
                    ? "bg-white text-black"
                    : "text-white/80 hover:text-white"
                }`}
                data-testid="shoutouts-reel-mode-video"
                aria-pressed={mode === "video"}
              >
                <Play className="w-3.5 h-3.5" /> VIDEO
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setMode("card");
                }}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                  mode === "card"
                    ? "bg-white text-black"
                    : "text-white/80 hover:text-white"
                }`}
                data-testid="shoutouts-reel-mode-card"
                aria-pressed={mode === "card"}
              >
                <LayoutGrid className="w-3.5 h-3.5" /> CARD
              </button>
            </div>
          </div>
        )}

        {/* Caption scrim — only in video mode so it doesn't fight the card art */}
        {current.caption && !showCardView && (
          <div
            className={`absolute left-0 right-0 px-4 ${
              hasCard ? "bottom-14" : "bottom-0"
            } pt-12 pb-4 z-10 bg-gradient-to-t from-black via-black/70 to-transparent text-white pointer-events-none`}
          >
            <p className="text-sm leading-snug max-w-md">{current.caption}</p>
          </div>
        )}
      </div>
    </div>
  );

  // Portal target: <body>. Falls back to inline render if document
  // isn't available (e.g. SSR), though this codebase is CSR-only.
  if (typeof document === "undefined" || !document.body) return modalNode;
  return createPortal(modalNode, document.body);
};

export default ShoutoutsReelModal;
